import React, { useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { TextField, Toggle } from './fields';
import { formatPhoneMaybe } from '../../utils/phone';
import { findInvalidEmails } from '../../utils/email';
import {
  CarrierContact, createCarrierContact, updateCarrierContact, deleteCarrierContact,
} from '../../services/carriercontacts.service';

/**
 * Small open-entry editor for a single carrier contact (highway.carriercontact):
 * Type / Name / Phone / Fax / Email + a Default flag. Self-saves via the
 * /carriercontacts router; used by CarrierContactsPanel. Carrier contacts only
 * exist once the carrier profile is saved, so this is edit-mode only.
 */
const s = (v: any): string => (v == null ? '' : String(v));

interface Props {
  carrierrecno: number;
  contact: CarrierContact | null; // null = create
  onClose: () => void;
  onSaved: () => void;
}

const CarrierContactDrawer: React.FC<Props> = ({ carrierrecno, contact, onClose, onSaved }) => {
  const isNew = !contact;
  const [form, setForm] = useState({
    contacttype: contact?.contacttype ?? 'DEFAULT',
    contactname: contact?.contactname ?? '',
    contactphone: contact?.contactphone ?? '',
    contactfax: contact?.contactfax ?? '',
    contactemail: contact?.contactemail ?? '',
    defaultcontact: (contact?.defaultcontact ?? 'N') === 'Y',
    deleted: (contact?.deleted ?? 'N') === 'Y',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const set = (k: keyof typeof form, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const nameInvalid = useMemo(() => !s(form.contactname).trim(), [form.contactname]);
  const emailInvalid = useMemo(
    () => findInvalidEmails(s(form.contactemail), { semicolon: true }).length > 0,
    [form.contactemail],
  );

  const handleSave = async () => {
    if (nameInvalid || emailInvalid) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      const payload = {
        carrierrecno,
        contacttype: s(form.contacttype),
        contactname: s(form.contactname),
        contactphone: s(form.contactphone),
        contactfax: s(form.contactfax),
        contactemail: s(form.contactemail),
        defaultcontact: form.defaultcontact ? 'Y' : 'N',
        deleted: form.deleted ? 'Y' : 'N',
      };
      if (isNew) await createCarrierContact(payload);
      else await updateCarrierContact(contact!.recno, payload);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save contact.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      await deleteCarrierContact(contact.recno);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to delete contact.');
    } finally {
      setIsSaving(false);
    }
  };

  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div>
        {!isNew && (
          <button onClick={handleDelete} disabled={isSaving}
            className="px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50">
            Delete
          </button>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button onClick={onClose} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
          Cancel
        </button>
        <button onClick={handleSave} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
            : (<><CheckIcon className="w-4 h-4" /> {isNew ? 'Add Contact' : 'Save Contact'}</>)}
        </button>
      </div>
    </div>
  );

  return (
    <Drawer isOpen onClose={onClose} title={isNew ? 'Add Contact' : 'Edit Contact'} position="right" width="28rem" footer={footer}>
      <div className="space-y-4">
        {saveError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {saveError}
          </div>
        )}
        <TextField label="Type" disabled={isSaving} value={s(form.contacttype)} maxLength={50}
          onChange={(v) => set('contacttype', v)} />
        <TextField label="Name" required invalid={showErrors && nameInvalid} disabled={isSaving}
          value={s(form.contactname)} maxLength={100} onChange={(v) => set('contactname', v)} />
        <TextField label="Phone" disabled={isSaving} value={s(form.contactphone)} maxLength={25}
          onChange={(v) => set('contactphone', v)} onBlur={() => set('contactphone', formatPhoneMaybe(s(form.contactphone)))} />
        <TextField label="Fax" disabled={isSaving} value={s(form.contactfax)} maxLength={25}
          onChange={(v) => set('contactfax', v)} onBlur={() => set('contactfax', formatPhoneMaybe(s(form.contactfax)))} />
        <div>
          <TextField label="Email" type="email" disabled={isSaving} value={s(form.contactemail)} maxLength={100}
            invalid={showErrors && emailInvalid} onChange={(v) => set('contactemail', v)} />
          {showErrors && emailInvalid && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">Enter a valid email address.</p>
          )}
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <Toggle label="Default contact" checked={form.defaultcontact} disabled={isSaving}
            onChange={(v) => set('defaultcontact', v)} />
          {!isNew && (
            <Toggle label="Deleted" checked={form.deleted} disabled={isSaving}
              onChange={(v) => set('deleted', v)} hint="Soft-deletes this contact; uncheck to restore." />
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CarrierContactDrawer;
